import {
	areMatchingEvents,
	hasEventDataChanged,
	mergeEventPayloadData,
	parseEvents,
} from '@/OptimusRoutes/components/mapDevices/utils/mapEvents.utils';
import { STANDARD_DATE_FORMAT, UTC_FORMAT } from '@/OptimusRoutes/constants';
import { LAST_EVENTS_LENGTH_SIZE } from '@/OptimusRoutes/pages/MainMap/components/ControlPanel/constants';
import { EventHistoryByDeviceDto } from '@/OptimusRoutes/pages/MainMap/types/trackingHistory.types';
import { Event, ParsedEvent } from '@/OptimusRoutes/types/rabbitMessage.types';
import { dateToString, getDifference, utcToLocalIana } from '@/utilities/dateUtils';

type MergeLastEvents = {
	existingEvents: EventHistoryByDeviceDto[];
	incomingEvents: Event[];
	utcDate: Date;
	ianaTimezone: string;
	trackingHistoryId: string | number;
	latitude: number;
	longitude: number;
};

export const mergeLastEvents = ({
	existingEvents,
	incomingEvents,
	utcDate,
	ianaTimezone,
	trackingHistoryId,
	latitude,
	longitude,
}: MergeLastEvents): EventHistoryByDeviceDto[] => {
	const parsedIncomingEvents = parseEvents(incomingEvents);
	let mergedEvents = existingEvents;
	const unstoredIncomingEvents: ParsedEvent[] = [];

	parsedIncomingEvents.forEach((incomingEvent) => {
		const matchIndex = mergedEvents.findIndex((storedEvent) =>
			areMatchingEvents({
				storedEventId: storedEvent.eventId,
				storedData: storedEvent.data,
				incomingEventId: incomingEvent.id,
				incomingData: incomingEvent.data,
				storedTrackingHistoryId: storedEvent.trackingHistoryId,
				incomingTrackingHistoryId: trackingHistoryId,
			})
		);

		if (matchIndex < 0) {
			unstoredIncomingEvents.push(incomingEvent);
			return;
		}

		const storedEvent = mergedEvents[matchIndex];
		const mergedData = mergeEventPayloadData(storedEvent.data, incomingEvent.data);

		if (!hasEventDataChanged(storedEvent.data, mergedData)) {
			return;
		}

		if (mergedEvents === existingEvents) {
			mergedEvents = [...existingEvents];
		}

		mergedEvents[matchIndex] = { ...storedEvent, data: mergedData };
	});

	if (unstoredIncomingEvents.length === 0) {
		if (mergedEvents === existingEvents) {
			return existingEvents;
		}

		return mergedEvents.sort((a, b) => +new Date(b.date) - +new Date(a.date)).slice(0, LAST_EVENTS_LENGTH_SIZE);
	}

	const incomingWithLocalDatetime: EventHistoryByDeviceDto[] = unstoredIncomingEvents.map((event) => ({
		eventId: event.id,
		date: dateToString(utcToLocalIana({ utcDate, ianaTimezone }), UTC_FORMAT),
		trackingHistoryId: trackingHistoryId.toString(),
		data: event.data,
		latitude,
		longitude,
	}));

	let nextEvents = incomingWithLocalDatetime;

	if (mergedEvents.length > 0) {
		const storedEventDate = dateToString(mergedEvents[0].date, STANDARD_DATE_FORMAT);
		const incomingEventDate = dateToString(
			incomingWithLocalDatetime[incomingWithLocalDatetime.length - 1].date,
			STANDARD_DATE_FORMAT
		);
		const incomingDayOffset = getDifference(storedEventDate, incomingEventDate, 'days');

		if (incomingDayOffset === 0) {
			nextEvents = [...incomingWithLocalDatetime, ...mergedEvents];
		}

		if (incomingDayOffset < 0) {
			if (mergedEvents === existingEvents) {
				return existingEvents;
			}

			nextEvents = mergedEvents;
		}
	}

	return nextEvents.sort((a, b) => +new Date(b.date) - +new Date(a.date)).slice(0, LAST_EVENTS_LENGTH_SIZE);
};
